import { Platform } from 'react-native'
import { getManagedValue } from './getManagedValue'
import { getUrlParam } from './getUrlParam'

export type ConfigEnvValues = Record<string, string | undefined>

/**
 * Where a value came from, for debug screens.
 * `n/a` marks a source that cannot exist on the current platform.
 */
export type ConfigSetting = {
  managed: string | undefined
  env: string | undefined
  url: string | undefined
  resolved: string | undefined
}

/** Flags are opt-in strings — anything but `1`/`true` reads as off. */
export const isTruthyFlag = (value: string | null | undefined) =>
  value === '1' || value === 'true'

/**
 * Binds the value lookups to an app's env map.
 *
 * Resolution order is always url param (web) -> managed app config (native) ->
 * `EXPO_PUBLIC_*` env -> default. The env map has to list the `process.env`
 * reads explicitly, they are inlined at build time and cannot be computed.
 */
export const createConfigValues = <T extends ConfigEnvValues>(envValues: T) => {
  type Key = keyof T & string

  /**
   * Managed app config -> env. Ignores url params, use `getParamOrEnv` for
   * anything that should be overridable from the browser.
   */
  const getValue = (key: Key, defaultValue?: string) =>
    getManagedValue(key) || envValues[key] || defaultValue

  /**
   * Url param only, `undefined` off web. Reach for it when an explicit
   * `?flag=0` has to win over the env/managed value.
   */
  const getUrlValue = (key: Key) =>
    Platform.OS === 'web' ? getUrlParam(key) : undefined

  const getParamOrEnv = (key: Key, defaultValue?: string) =>
    getUrlValue(key) || getValue(key, defaultValue)

  const getBoolean = (key: Key, defaultValue = false) => {
    const value = getParamOrEnv(key)
    if (value == null) {
      return defaultValue
    }
    return isTruthyFlag(value)
  }

  const getNumber = (key: Key, defaultValue: number) => {
    const value = getParamOrEnv(key)
    if (value == null) {
      return defaultValue
    }
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : defaultValue
  }

  const getSetting = (key: Key, defaultValue?: string): ConfigSetting => {
    const managed = getManagedValue(key)
    const env = envValues[key]
    const url = getUrlValue(key)

    return {
      managed: Platform.select({ default: managed, web: 'n/a' }),
      env,
      url: Platform.select({ default: 'n/a', web: url }),
      resolved: url || managed || env || defaultValue,
    }
  }

  return {
    getValue,
    getUrlValue,
    getParamOrEnv,
    getBoolean,
    getNumber,
    getSetting,
  }
}

export type ConfigValues<T extends ConfigEnvValues> = ReturnType<
  typeof createConfigValues<T>
>
